import { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/components/auth/AuthProvider';
import LanguageSelector from '@/components/language-selector/LanguageSelector';
import { useNotification } from '@/components/ui-library/NotificationProvider';
import { DEFAULT_LANGUAGE, type SupportedLanguageCode } from '@/constants/languages';
import useAnalytics from '@/hooks/useAnalytics';
import { setNavMode, type NavMode } from '@/hooks/useNavMode';
import { changeLanguage } from '@/i18n';
import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';

import NavModeToggle from './NavModeToggle';
import SaveStatus, { type SaveState } from './SaveStatus';
import { SettingsRow, SettingsSection, useSettingsSurface } from './SettingsSection';
import type { SettingsPanelProps } from './settingsTypes';

export function LanguageSettingsSection({ settings }: SettingsPanelProps) {
  const { t, i18n } = useTranslation('settings');
  const { t: tCommon } = useTranslation('common');
  const { user } = useAuth();
  const analytics = useAnalytics();
  const { showNotification } = useNotification();
  const surface = useSettingsSurface();
  const [changingLanguage, setChangingLanguage] = useState(false);
  const [localSaveState, setLocalSaveState] = useState<SaveState>('idle');
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (savedTimer.current) clearTimeout(savedTimer.current);
    },
    []
  );

  const selectLanguage = async (code: SupportedLanguageCode) => {
    if (code === settings.language || changingLanguage) return;
    if (user) {
      if (settings.groups.language.state !== 'saving') settings.selectLanguage(code);
      return;
    }
    // Guest preferences stay in this browser; they must never post account settings.
    setChangingLanguage(true);
    if (savedTimer.current) clearTimeout(savedTimer.current);
    setLocalSaveState('saving');
    try {
      await changeLanguage(code);
      analytics.track('Language Updated', { source: 'settings', language: code });
      setLocalSaveState('saved');
      savedTimer.current = setTimeout(() => setLocalSaveState('idle'), 2400);
    } catch (error) {
      console.error('Error changing settings language:', error);
      setLocalSaveState('error');
      if (surface === 'page') showNotification(t('unableToSave'), 'error');
    } finally {
      setChangingLanguage(false);
    }
  };

  return (
    <SettingsSection>
      <SettingsRow
        label={t('language')}
        labelId="settings-language-label"
        description={t('languageDesc')}
        headerIcon={<LanguageIcon sx={{ fontSize: 20 }} />}
        control={
          <Box sx={{ width: 220, maxWidth: '100%' }}>
            <LanguageSelector
              value={settings.language}
              onChange={(code) => void selectLanguage(code)}
              labelId="settings-language-label"
              disabled={changingLanguage || settings.groups.language.state === 'saving'}
            />
          </Box>
        }
        status={
          user || surface === 'dialog' ? (
            <SaveStatus
              state={user ? settings.groups.language.state : localSaveState}
              error={user ? settings.groups.language.error : t('unableToSave')}
              savingLabel={t('saving')}
              savedLabel={t('saved')}
            />
          ) : undefined
        }
      />
      {i18n.language !== DEFAULT_LANGUAGE && (
        <Typography
          sx={{
            mt: 1.5,
            fontSize: surface === 'dialog' ? '13px' : '12px',
            lineHeight: 1.5,
            color: semanticColors.textTertiary,
          }}
        >
          {tCommon('translationNotice')}
        </Typography>
      )}
    </SettingsSection>
  );
}

export function NavigationSettingsSection({ settings }: SettingsPanelProps) {
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const analytics = useAnalytics();
  const surface = useSettingsSurface();

  const selectMode = (mode: NavMode) => {
    if (user) {
      if (settings.groups.navMode.state !== 'saving') settings.selectNavMode(mode);
      return;
    }
    if (mode === settings.navMode) return;
    const previous = settings.navMode;
    setNavMode(mode);
    analytics.track('Nav Mode Selected', { mode, previousMode: previous, authed: false });
  };

  return (
    <SettingsSection>
      {/* On the full page the phone preview sits to the right of the row. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <SettingsRow
            headerIcon={<SmartphoneOutlinedIcon sx={{ fontSize: 20 }} />}
            label={t('navMode')}
            labelId="settings-nav-mode-label"
            description={t('navModeScope')}
            divider={false}
            control={
              <Box sx={{ width: 220, maxWidth: '100%' }}>
                <NavModeToggle
                  value={settings.navMode}
                  onChange={selectMode}
                  tabLabel={t('navModeTab')}
                  sheetLabel={t('navModeSheet')}
                  labelId="settings-nav-mode-label"
                  descriptionId={
                    surface === 'dialog' ? 'settings-nav-mode-preview-desc' : undefined
                  }
                  comfortable={surface === 'dialog'}
                  disabled={settings.groups.navMode.state === 'saving'}
                />
              </Box>
            }
            status={
              user ? (
                <SaveStatus
                  state={settings.groups.navMode.state}
                  error={settings.groups.navMode.error}
                  savingLabel={t('saving')}
                  savedLabel={t('saved')}
                />
              ) : undefined
            }
            details={
              surface === 'dialog' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PhoneLayoutPreview mode={settings.navMode} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                      {t(settings.navMode === 'tab' ? 'navModeTab' : 'navModeSheet')}
                    </Typography>
                    <Typography
                      id="settings-nav-mode-preview-desc"
                      sx={{
                        fontSize: '14px',
                        lineHeight: 1.5,
                        color: semanticColors.textTertiary,
                        mt: 0.5,
                      }}
                    >
                      {t(settings.navMode === 'tab' ? 'navModeTabDesc' : 'navModeSheetDesc')}
                    </Typography>
                  </Box>
                </Box>
              ) : undefined
            }
          />
        </Box>
        {surface === 'page' && <PhoneLayoutPreview mode={settings.navMode} />}
      </Box>
    </SettingsSection>
  );
}

function PhoneLayoutPreview({ mode }: { mode: NavMode }) {
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative',
        // Roughly a modern phone's ~9:19.5 aspect.
        width: 48,
        height: 84,
        flexShrink: 0,
        border: `2px solid ${semanticColors.textTertiary}`,
        borderRadius: `calc(${BORDER_RADIUS} * 1.25)`,
        overflow: 'hidden',
        p: 0.75,
        backgroundColor: semanticColors.surface,
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 4,
          mx: 'auto',
          borderRadius: BORDER_RADIUS,
          backgroundColor: semanticColors.borderMedium,
          mb: 1,
        }}
      />
      {[24, 30, 20].map((width) => (
        <Box
          key={width}
          sx={{
            width,
            height: 4,
            mb: 0.5,
            borderRadius: `calc(${BORDER_RADIUS} / 2)`,
            backgroundColor: semanticColors.borderLight,
          }}
        />
      ))}
      <Box
        sx={{
          position: 'absolute',
          insetInline: 0,
          bottom: 0,
          height: mode === 'sheet' ? 34 : 16,
          backgroundColor: semanticColors.surfaceWarm,
          borderTop: `1px solid ${semanticColors.borderMedium}`,
          px: 0.75,
          py: 0.5,
        }}
      >
        {mode === 'tab' ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 6,
                  borderRadius: `calc(${BORDER_RADIUS} / 2)`,
                  backgroundColor:
                    index === 1 ? semanticColors.textPrimary : semanticColors.borderMedium,
                }}
              />
            ))}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                width: 14,
                height: 3,
                mx: 'auto',
                borderRadius: BORDER_RADIUS,
                backgroundColor: semanticColors.textTertiary,
                mb: 0.5,
              }}
            />
            <Box
              sx={{
                height: 15,
                borderRadius: `calc(${BORDER_RADIUS} / 2)`,
                border: `1px solid ${semanticColors.borderMedium}`,
                backgroundColor: semanticColors.surface,
              }}
            />
          </>
        )}
      </Box>
    </Box>
  );
}
